import React from 'react';
import { AlertTriangle, X } from 'lucide-react';
import Button from './Button';

interface DeleteConfirmModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  documentName: string;
  isDeleting?: boolean;
}

const DeleteConfirmModal: React.FC<DeleteConfirmModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  documentName,
  isDeleting = false
}) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="fixed inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-eg-blanc rounded-md shadow-xl max-w-md w-full mx-4">
        <div className="flex items-center justify-between p-6 border-b">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-eg-accent/10 rounded-full">
              <AlertTriangle className="w-5 h-5 text-eg-accent" />
            </div>
            <h3 className="text-lg font-semibold text-eg-encre">Supprimer le document</h3>
          </div>
          <button
            onClick={onClose}
            className="text-eg-estompe hover:text-eg-gris transition-colors"
            disabled={isDeleting}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6">
          <div className="space-y-4">
            <p className="text-eg-brun">
              Êtes-vous sûr de vouloir supprimer le document{' '}
              <span className="font-semibold text-eg-encre">"{documentName}"</span> ?
            </p>
            
            <div className="bg-eg-accent/10 border border-eg-accent/30 rounded-md p-4">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-eg-accent mt-0.5 flex-shrink-0" />
                <div className="text-sm text-eg-accent">
                  <p className="font-medium mb-1">Cette action est irréversible</p>
                  <p>Le document sera définitivement supprimé et ne pourra pas être récupéré.</p>
                </div>
              </div>
            </div>

            <div className="bg-eg-papier rounded-md p-4">
              <h4 className="font-medium text-eg-encre mb-2">Conséquences :</h4>
              <ul className="text-sm text-eg-brun space-y-1">
                <li>• Le document sera supprimé de tous les dossiers</li>
                <li>• Les liens de partage ne fonctionneront plus</li>
                <li>• L'historique des modifications sera perdu</li>
                <li>• Les commentaires et annotations seront supprimés</li>
              </ul>
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-3 p-6 border-t">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={isDeleting}
          >
            Annuler
          </Button>
          <Button
            onClick={onConfirm}
            disabled={isDeleting}
            className="bg-red-600 hover:bg-red-700 text-white"
          >
            {isDeleting ? (
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                Suppression...
              </div>
            ) : (
              'Supprimer définitivement'
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default DeleteConfirmModal;
